import config from '@config'
import useAds from '@hooks/useAds'
import useAdViewport from '@hooks/useAdViewport'
import usePageMetadata, {
  Breadcrumbs,
  PageMetadata,
} from '@hooks/usePageMetadata'
import { getCurrentAdViewportType, getPageType, logAdMessage } from '@utils/ads'
import { getCookieValue } from '@utils/cookie'
import { getLocalStorageItem } from '@utils/localStorage'
import { FunctionComponent, useCallback, useEffect } from 'react'
import { AdmTagMan } from './AppNexus/types'

const {
  ads: {
    admeira: {
      storageKey: admeiraStorageKey,
      appnexus: {
        fallbackChannel,
        articleIdKeyName: appnexusArticleIdKeyName,
        channelMapping,
      },
    },
  },
} = config

const getChannel = (breadcrumbs: Breadcrumbs = []): string => {
  // If breadcrumbs are missing, return fallback channel
  if (!(breadcrumbs?.length > 0)) {
    return fallbackChannel
  } else if (breadcrumbs.length === 1) {
    // Catch if we are on home section
    return breadcrumbs[0].title
  } else {
    // Calculate the channel
    return [...breadcrumbs].reverse().reduce((acc, breadcrumb) => {
      const channel =
        channelMapping[breadcrumb.title as keyof typeof channelMapping]
      return channel && acc === fallbackChannel ? channel : acc
    }, fallbackChannel)
  }
}

const initAds = (pageMetadata: PageMetadata) => {
  const { id, breadcrumbs, type, sectionIds, adChannel, teaser, syndication } =
    pageMetadata
  const contentType = teaser?.targetContentType
  const shouldAdmForce = getCookieValue('BLICKFORCEADS')
  const admeiraId = admeiraStorageKey
    ? getLocalStorageItem<{ admeiraId: string }>(admeiraStorageKey)?.admeiraId
    : ''

  const initObj = {
    platform:
      getCurrentAdViewportType() === 'desktop' ? 'Desktop' : 'MobileWeb',
    channel: adChannel ?? getChannel(breadcrumbs),
    targeting: {
      pagetype: getPageType(type),
      ...(id ? { [appnexusArticleIdKeyName]: id } : {}),
      ...(contentType ? { contenttype: contentType } : {}),
      ...(syndication ? { syndication } : {}),
      subsectionid: sectionIds,
      ...(shouldAdmForce ? { admforce: 'qa' } : {}),
    },
    ...(admeiraId ? { uuid: admeiraId } : {}),
  }

  window.admTagMan.q.push(() => {
    const admTagMan = window.admTagMan as AdmTagMan
    admTagMan.init(initObj)
  })
}

const AdManager: FunctionComponent = () => {
  const adViewport = useAdViewport()
  const pageMetadata = usePageMetadata()
  const { updateAreAdSlotsLoaded, triggerAdRegistration } = useAds()

  const resetAreAdSlotsLoaded = useCallback<
    (pageMetadataValue: PageMetadata) => void
  >(
    (pageMetadataValue) => {
      updateAreAdSlotsLoaded(false)
      initAds(pageMetadataValue)
      logAdMessage(`ADS: Tag Manager init for: ${getCurrentAdViewportType()}`)
      triggerAdRegistration()
    },
    [triggerAdRegistration, updateAreAdSlotsLoaded]
  )

  useEffect(() => {
    if (adViewport) {
      resetAreAdSlotsLoaded(pageMetadata)
    }
  }, [adViewport, pageMetadata, resetAreAdSlotsLoaded])

  return null
}

export default AdManager
